import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { BaseContenutoConProgress, BaseFeedbackConIcona } from 'feedback'
import { ICONE } from 'icons'
import { BaseButtons, BaseIconButtons, BaseRadioGroup, BaseTextInput } from 'inputs'
import { BaseGridLayout, BaseModal, useModal, BaseScatolaConTitolo } from 'layout'
import { BaseMappa, BaseMappaEvento, BaseMappaMarker, BaseMappaMarkerIcona } from 'maps'
import { BaseCoppiaChiaveValore, BaseGrassetto, BaseTesto, BaseTitolo } from 'text'
import { OPERAZIONI_BASE } from '../../network/OperazioniBase'
import useRichiestaServer from '../../network/RichiestaServerHook'
import { formattaCoordinata, formattaIndirizzo, isLocalizzazioneValida } from './utilIndirizzo'

export default function LocalizzazioneModifica(props) {
  const { modalRef, openModal, closeModal } = useModal()

  const { getValues } = useFormContext()

  const indirizzo = getValues()

  return (
    <>
      <BaseButtons.Custom
        testo='Coordinate'
        iconaInizio={ICONE.LUOGO}
        variant='outlined'
        onClick={openModal}
      />

      {!isLocalizzazioneValida(indirizzo.localizzazione) &&
        <BaseIconButtons.Custom
          contenutoTooltip='Coordinate non compilate'
          Icon={ICONE.ATTENZIONE}
        />
      }

      <BaseModal
        ref={modalRef}
        titolo='Coordinate indirizzo'
        maxWidth='md'
      >
        <ContenutoModalLocalizzazione {...props} closeModal={closeModal} />
      </BaseModal>
    </>
  )
}



const MODALITA = {
  MAPPA: 'MAPPA',
  MANUALE: 'MANUALE',
}

function ContenutoModalLocalizzazione(props) {
  const { nameLatitudine, nameLongitudine, closeModal } = props

  const [modalita, setModalita] = useState(MODALITA.MAPPA)

  const { onChange, watch, getValues, resetField } = useFormContext()

  const latitudine = watch(nameLatitudine) || getValues(nameLatitudine)
  const longitudine = watch(nameLongitudine) || getValues(nameLongitudine)

  function compilaLocalizzazione(localizzazione) {
    const { latitudine, longitudine } = localizzazione
    resetField(nameLatitudine, { defaultValue: latitudine })
    resetField(nameLongitudine, { defaultValue: longitudine })
    onChange()
    setTimeout(() => closeModal(), 1000)
  }

  const indirizzo = getValues()
  const { tipo, via, cap, comune, provincia } = indirizzo
  const campiObbligatoriCompilati = tipo && via && cap && comune && provincia

  const refCompilazioneTramiteMappa = useRef()

  return (
    <>
      <BaseGridLayout justifyCenter disposizioneRighe={[1, 1, 1, 2]} sx={{ mb: 1 }}>
        <BaseTesto>
          <BaseGrassetto>{formattaIndirizzo(indirizzo)}</BaseGrassetto>
          {modalita === MODALITA.MAPPA && campiObbligatoriCompilati &&
            <BaseButtons.Custom
              testo='Mostra sulla mappa'
              onClick={() => refCompilazioneTramiteMappa.current.centraMappaSuIndirizzo()}
              variant='outlined'
              sx={{ ml: 2 }}
            />
          }
        </BaseTesto>

        <BaseScatolaConTitolo titolo='Coordinate compilate' outlined sx={{ my: 2 }}>
          <BaseGridLayout alignCenter spacing={5}>
            <BaseCoppiaChiaveValore chiave='Latitudine' valore={formattaCoordinata(latitudine)} />
            <BaseCoppiaChiaveValore chiave='Longitudine' valore={formattaCoordinata(longitudine)} />
          </BaseGridLayout>
        </BaseScatolaConTitolo>

        <BaseGridLayout alignCenter>
          <BaseTitolo colorato>Modifica coordinate</BaseTitolo>
          <BaseRadioGroup
            options={[
              { value: MODALITA.MAPPA, label: 'Tramite mappa' },
              { value: MODALITA.MANUALE, label: 'Manuale' },
            ]}
            value={modalita}
            onChange={e => setModalita(e.target.value)}
            nonUsareHookForm
            visualizzazioneOrizzontale
          />
        </BaseGridLayout>

        <BaseTextInput
          label='Latitudine' name={nameLatitudine}
          type='numeroPositivo'
          hidden={modalita !== MODALITA.MANUALE}
        />
        <BaseTextInput
          label='Longitudine' name={nameLongitudine}
          type='numeroPositivo'
          hidden={modalita !== MODALITA.MANUALE}
        />
      </BaseGridLayout>

      {modalita === MODALITA.MAPPA &&
        <CompilazioneTramiteMappa
          indirizzo={indirizzo}
          compilaLocalizzazione={compilaLocalizzazione}
          campiObbligatoriCompilati={campiObbligatoriCompilati}
          refCompilazioneTramiteMappa={refCompilazioneTramiteMappa}
        />
      }
    </>
  )
}



function CompilazioneTramiteMappa(props) {
  const {
    indirizzo,
    compilaLocalizzazione,
    campiObbligatoriCompilati,
    refCompilazioneTramiteMappa,
  } = props

  const [localizzazione, setLocalizzazione] = useState(indirizzo.localizzazione)

  const {
    ScatolaMessaggi,
    inviaOperazione,
    inAttesaDelServer
  } = useRichiestaServer()

  useEffect(() => {
    async function ottieniCoordinate() {
      if (isLocalizzazioneValida(indirizzo.localizzazione) || !campiObbligatoriCompilati) return
      const { ok, data } = await inviaOperazione(OPERAZIONI_BASE.INDIRIZZO_LOCALIZZAZIONE, { 
        indirizzo: trasformaIndirizzoPerChiamata(indirizzo)
      })
      if (ok) setLocalizzazione(data.localizzazione)
    }
    ottieniCoordinate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indirizzo, campiObbligatoriCompilati])

  const mappaRef = useRef()

  useImperativeHandle(refCompilazioneTramiteMappa, () => ({
    async centraMappaSuIndirizzo() {
      const { ok, data } = await inviaOperazione(OPERAZIONI_BASE.INDIRIZZO_LOCALIZZAZIONE, { 
        indirizzo: trasformaIndirizzoPerChiamata(indirizzo)
      }, { setInAttesa: () => { } })
      if (ok) mappaRef.current.centraInPosizione(data.localizzazione)
    }
  }))

  function centraSuCoordinateCompilate() {
    if (isLocalizzazioneValida(indirizzo.localizzazione)) {
      mappaRef.current.centraInPosizione(indirizzo.localizzazione)
    }
  }

  function centraSuCoordinateModificate() {
    mappaRef.current.centraInPosizione(localizzazione)
  }

  const propsBottoneMostra = {
    testo: 'Mostra',
    variant: 'outlined',
    sx: { ml: 1 },
  }

  return (
    <>
      {!campiObbligatoriCompilati && !isLocalizzazioneValida(localizzazione) &&
        <BaseGridLayout justifyCenter>
          <BaseFeedbackConIcona.Errore
            messaggio='Compilare i campi obbligatori (Tipo, Via, CAP, Comune, Provincia)'
          />
        </BaseGridLayout>
      }

      {!indirizzo.numeroCivico &&
        <BaseGridLayout justifyCenter>
          <BaseFeedbackConIcona.Attenzione
            messaggio='Compilare il Civico potrebbe migliorare la precisione delle coordinate iniziali'
          />
        </BaseGridLayout>
      }

      {ScatolaMessaggi}

      <BaseContenutoConProgress inAttesa={inAttesaDelServer}>
        {isLocalizzazioneValida(localizzazione) &&
          <>
            <BaseTesto>Cliccare sulla mappa per modificare le coordinate, poi premere "Salva coordinate"</BaseTesto>

            <BaseGridLayout justifyCenter spacing={5}>
              <BaseGridLayout alignCenter spacing={0}>
                <BaseMappaMarkerIcona />
                <BaseTesto key='info'> = coordinate compilate</BaseTesto>
                <BaseButtons.Custom onClick={centraSuCoordinateCompilate} {...propsBottoneMostra} />
              </BaseGridLayout>
              <BaseGridLayout alignCenter spacing={0}>
                <BaseMappaMarkerIcona color='secondary' />
                <BaseTesto key='info'> = coordinate modificate</BaseTesto>
                <BaseButtons.Custom onClick={centraSuCoordinateModificate} {...propsBottoneMostra} />
              </BaseGridLayout>
            </BaseGridLayout>

            <BaseMappa centro={localizzazione} mappaRef={mappaRef}>
              <BaseMappaMarker posizione={localizzazione} color='secondary' />
              {isLocalizzazioneValida(indirizzo.localizzazione) &&
                <BaseMappaMarker posizione={indirizzo.localizzazione} />
              }
              <BaseMappaEvento on='click' handler={({ posizione }) => setLocalizzazione(posizione)} />
            </BaseMappa>

            <BaseButtons.Salva
              testo='Salva coordinate'
              submit={false}
              onClick={() => compilaLocalizzazione(localizzazione)}
            />
          </>
        }
      </BaseContenutoConProgress>
    </>
  )
}



function trasformaIndirizzoPerChiamata(indirizzo) {
  const { localizzazione } = indirizzo

  return {
    ...indirizzo,
    localizzazione: {
      ...localizzazione,
      ...(localizzazione?.latitudine === '' && { latitudine: 0 }),
      ...(localizzazione?.longitudine === '' && { longitudine: 0 }),
    }
  }
}